import React from "react";

import { ArrowLeft, CheckCircle2, Mail } from "lucide-react";

import {
  AuthLayout,
  ForgotPasswordErrorAlert,
  FormField,
  PrimaryButton,
  StatusAlert,
} from "@/components/auth";
import { FORGOT_STATES, useForgotPassword } from "@/hooks/useForgotPassword";
import { withAppBase } from "@/lib/appPaths";

export default function ForgotPassword() {
  const {
    email,
    setEmail,
    currentState,
    isSubmitting,
    isEmailValid,
    handleSubmit,
    navigate,
    setCurrentState,
  } = useForgotPassword();

  if (currentState === FORGOT_STATES.SUCCESS) {
    return (
      <AuthLayout
        footer={
          <div className="text-center">
            <a
              href={withAppBase("/login")}
              data-testid="back-to-signin-link"
              className="inline-flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Sign in
            </a>
          </div>
        }
      >
        <div data-testid="success-state" className="text-center py-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 dark:text-emerald-400" />
          </div>
          <h2 className="text-xl font-bold font-heading text-slate-900 dark:text-slate-100 mb-2">
            Check your email
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-sm mx-auto">
            If an admin account exists for{" "}
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {email || "this email"}
            </span>
            , password reset instructions have been sent.
          </p>
          <StatusAlert
            variant="info"
            message="The email may take a few minutes to arrive. Check your spam folder if you don't see it."
            className="text-left mb-6"
          />
          <PrimaryButton
            data-testid="back-to-signin-btn"
            onClick={() => navigate("/login")}
          >
            Back to Sign in
          </PrimaryButton>
          <p className="mt-4 text-xs text-slate-400 dark:text-slate-500">
            Didn't receive the email?{" "}
            <button
              data-testid="resend-link"
              onClick={() => setCurrentState(FORGOT_STATES.DEFAULT)}
              className="text-indigo-600 dark:text-indigo-400 hover:underline"
              type="button"
            >
              Try again
            </button>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      footer={
        <div className="text-center">
          <a
            href={withAppBase("/login")}
            data-testid="back-to-signin-link"
            className="inline-flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign in
          </a>
        </div>
      }
    >
      <div data-testid="forgot-password-header" className="mb-6">
        <div className="w-12 h-12 mb-4 rounded-full bg-indigo-100 dark:bg-indigo-950 flex items-center justify-center">
          <Mail className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <h2 className="text-xl font-bold font-heading text-slate-900 dark:text-slate-100 mb-2">
          Forgot password?
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Enter your admin email address and we'll send you instructions to
          reset your password.
        </p>
      </div>

      {currentState !== FORGOT_STATES.DEFAULT && (
        <div data-testid="forgot-error-alert" className="mb-6">
          <ForgotPasswordErrorAlert state={currentState} />
        </div>
      )}

      <form
        data-testid="forgot-password-form"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <FormField
          id="email"
          label="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@company.com"
          autoComplete="email"
          error={
            email.length > 0 && !isEmailValid
              ? "Please enter a valid email address"
              : undefined
          }
        />
        <div className="pt-2">
          <PrimaryButton
            data-testid="submit-btn"
            type="submit"
            disabled={!isEmailValid}
            loading={isSubmitting}
          >
            Send reset instructions
          </PrimaryButton>
        </div>
      </form>

      <div className="mt-6 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
          Need help?
        </h4>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          If you're having trouble accessing your account, please contact your
          administrator or{" "}
          <a
            href="mailto:support@smartstore.com"
            className="text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            reach out to support
          </a>
          .
        </p>
      </div>
    </AuthLayout>
  );
}
